import { useState, useId } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

// Task 6: forced or voluntary password change. If the user's session flag
// mustChangePassword is set, they're routed here by ProtectedRoute and blocked
// from navigating elsewhere until they change it.
export default function ChangePasswordPage() {
  const uid = useId();
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useDocumentTitle('Change password');

  if (!user) return <Navigate to="/login" replace />;

  const forced = !!user.mustChangePassword;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      showToast('error', 'New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirm) {
      showToast('error', 'New password and confirmation do not match');
      return;
    }
    setSubmitting(true);
    try {
      const r = await apiService.changePassword(currentPassword, newPassword);
      if (r.success) {
        showToast('success', 'Password changed. Please sign in again.');
        // Force a fresh login so the JWT no longer carries a session tied to
        // the old credentials, and the mustChangePassword flag is re-read.
        logout();
        navigate('/login', { replace: true });
      } else {
        showToast('error', r.error || 'Failed to change password');
      }
    } catch (err: any) {
      showToast('error', err?.response?.data?.error || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-lg shadow p-6 space-y-4">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Change password</h1>
          {forced && (
            <p className="text-sm text-amber-700 mt-1">
              You must change your password before continuing.
            </p>
          )}
        </div>
        <div>
          <label htmlFor={`${uid}-1`} className="block text-xs font-medium text-gray-700 mb-1">Current password</label>
          <input
            id={`${uid}-1`}
            type="password"
            autoComplete="current-password"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor={`${uid}-2`} className="block text-xs font-medium text-gray-700 mb-1">New password</label>
          <input
            id={`${uid}-2`}
            type="password"
            autoComplete="new-password"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            minLength={8}
            required
          />
          <p className="text-xs text-gray-500 mt-1">Minimum 8 characters.</p>
        </div>
        <div>
          <label htmlFor={`${uid}-3`} className="block text-xs font-medium text-gray-700 mb-1">Confirm new password</label>
          <input
            id={`${uid}-3`}
            type="password"
            autoComplete="new-password"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Changing…' : 'Change password'}
        </button>
        {/* Someone who chose to be here can leave; someone who must change it first cannot go on without. */}
        {!forced && (
          <Link to="/" className="block py-2 text-center text-sm text-gray-600 hover:text-gray-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded">
            Back to the portal
          </Link>
        )}
      </form>
    </main>
  );
}
